import { computed, Injectable, signal } from '@angular/core';
import type { Signal } from '@angular/core';
import { defer, finalize } from 'rxjs';
import type { MonoTypeOperatorFunction } from 'rxjs';

/** One application-wide, reference-counted loader. */
@Injectable({ providedIn: 'root' })
export class RmsSpinnerService {
  private readonly counts = signal<Readonly<Record<string, number>>>({});
  readonly active = computed(() => Object.values(this.counts()).some(count => count > 0));
  readonly visible = signal(false);
  readonly message = signal('Loading your workspace…');
  private showTimer: ReturnType<typeof setTimeout> | null = null;

  // Late completion of a request from before reset() must not affect a new session.
  private generation = 0;

  /** Existing invoice components use these named request-state signals. */
  loading(...keys: string[]): Signal<boolean> {
    return computed(() => keys.some(key => (this.counts()[key] ?? 0) > 0));
  }

  begin(key: string, message = 'Loading your workspace…', immediate = false): () => void {
    const requestGeneration = this.generation;
    const wasIdle = !this.active();
    this.counts.update(counts => ({ ...counts, [key]: (counts[key] ?? 0) + 1 }));
    if (wasIdle) {
      this.message.set(message);
      this.showTimer = setTimeout(() => {
        this.showTimer = null;
        if (requestGeneration === this.generation && this.active()) {
          this.visible.set(true);
        }
      }, 180);
    }
    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      if (requestGeneration !== this.generation) return;
      this.counts.update(counts => {
        const next = { ...counts };
        const remaining = Math.max(0, (next[key] ?? 0) - 1);
        if (remaining > 0) next[key] = remaining;
        else delete next[key];
        return next;
      });
      if (!this.active()) {
        if (this.showTimer !== null) {
          clearTimeout(this.showTimer);
          this.showTimer = null;
        }
        this.visible.set(false);
        this.message.set('Loading your workspace…');
      }
    };
  }

  /** For non-HTTP RxJS work; always releases even after unsubscribe. */
  track<T>(key: string, message?: string): MonoTypeOperatorFunction<T> {
    return source => defer(() => {
      const stop = this.begin(key, message);
      return source.pipe(finalize(stop));
    });
  }

  /** Immediately stop all loading when an authenticated session ends. */
  reset(): void {
    this.generation++;
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    this.counts.set({});
    this.visible.set(false);
    this.message.set('Loading your workspace…');
  }
}
